import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Facebook, ArrowRight, Globe } from "lucide-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { BrandLogo } from "@/components/brand/Logo";
import api from "@/lib/api";
import { toast } from "sonner";

const EXPLORE = [
  { label: "All Villas", to: "/villas" },
  { label: "Luxury Collection", to: "/villas?category=luxury" },
  { label: "Weekend Getaways", to: "/villas?category=weekend" },
  { label: "Pet Friendly", to: "/villas?category=pet" },
  { label: "Interactive Map", to: "/map" },
];
const COMPANY = [
  { label: "About", to: "/p/about" },
  { label: "Hosts", to: "/p/hosts" },
  { label: "Owner Portal", to: "/owner" },
  { label: "Careers", to: "/p/careers" },
  { label: "Press", to: "/p/press" },
];
const SUPPORT = [
  { label: "Help Centre", to: "/p/help" },
  { label: "Cancellation", to: "/p/cancellation" },
  { label: "Privacy", to: "/p/privacy" },
  { label: "Terms", to: "/p/terms" },
  { label: "Contact", to: "/p/help" },
];

function LinkColumn({ title, links }) {
  return (
    <Box>
      <Box className="eyebrow" sx={{ mb: 2 }}>{title}</Box>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {links.map((l) => (
          <Box component="li" key={l.label}>
            <Box component={Link} to={l.to} sx={{ fontSize: 14, textDecoration: "none", color: "text.primary", "&:hover": { color: "primary.main" } }}>
              {l.label}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); toast("Welcome aboard — check your inbox."); setEmail(""); }
    catch { toast("Couldn't subscribe. Try again."); }
  };

  return (
    <Box component="footer" data-testid="footer" sx={{ borderTop: 1, borderColor: "divider", mt: { xs: 4, lg: 5 } }}>
      <Box sx={{
        mx: "auto", maxWidth: 1400, px: { xs: 3, lg: 6 }, py: 10,
        display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 6,
      }}>
        <Box sx={{ gridColumn: { xs: "span 2", md: "span 1" } }}>
          <BrandLogo size={26} />
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320, mt: 2.5, lineHeight: 1.7 }}>
            Curated luxury villas. Hand-selected, privately staffed, unforgettable.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
            <IconButton aria-label="Instagram" size="small"><Instagram size={18} /></IconButton>
            <IconButton aria-label="Twitter" size="small"><Twitter size={18} /></IconButton>
            <IconButton aria-label="Facebook" size="small"><Facebook size={18} /></IconButton>
          </Box>
        </Box>
        <LinkColumn title="Explore" links={EXPLORE} />
        <LinkColumn title="Company" links={COMPANY} />
        <LinkColumn title="Support" links={SUPPORT} />
        <Box sx={{ gridColumn: { xs: "span 2", md: "span 1" } }}>
          <Box className="eyebrow" sx={{ mb: 2 }}>Join our journey</Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Get travel ideas, new villas and special offers.
          </Typography>
          <Box component="form" onSubmit={submitNewsletter} sx={{ display: "flex", gap: 1 }}>
            <Box
              component="input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
              data-testid="footer-newsletter-input"
              sx={{
                flex: 1, minWidth: 0, px: 2, py: 1.1, borderRadius: 999, border: 1, borderColor: "divider",
                bgcolor: "background.paper", color: "inherit", outline: "none", fontFamily: "inherit", fontSize: 13,
                "&:focus": { borderColor: "primary.main" },
              }}
            />
            <IconButton type="submit" data-testid="footer-newsletter-submit" sx={{ bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.main" }, width: 38, height: 38, flexShrink: 0 }}>
              <ArrowRight size={15} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <Box sx={{ borderTop: 1, borderColor: "divider", py: 3, px: { xs: 3, lg: 6 }, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, maxWidth: 1400, mx: "auto" }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} Nirvagoo. All rights reserved.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "text.secondary" }}>
          <Globe size={13} />
          <Typography variant="caption">India (INR)</Typography>
        </Box>
      </Box>
    </Box>
  );
}
